import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { ativarSeguroAposPrimeiraParcela, criarEventoRenovacaoAgenda } from '@/hooks/useParcelas';
import { mergeContasAReceber, sortFinanceiroLinhas, financeiroSortToDbColumn, type FinanceiroSort } from '@/lib/financeiroRecebiveis';
import { fetchEscopoIdsVendedor, orFiltroEscopoPedidoContrato } from '@/lib/vendedorScope';
import { useAuth } from '@/hooks/useAuth';
import { isVendedorScopedRole } from '@/types/auth';
import { useMemo } from 'react';

export interface TransacaoFinanceira {
  id: number;
  cliente_id?: number | null;
  fornecedor_id?: number | null;
  pedido_id?: number;
  contrato_id?: number;
  sinistro_id?: number | null;
  tipo: 'entrada' | 'saida';
  valor: number;
  forma_pagamento: string;
  data_vencimento: string;
  data_pagamento?: string;
  status: string;
  observacoes?: string;
  numero_parcela?: number;
  total_parcelas?: number;
  nota_fiscal_url?: string;
  nota_fiscal_name?: string;
  notas_fiscais?: Array<{ url: string; nome: string }>;
  created_at: string;
  updated_at: string;
  
  // Relacionamentos
  cliente?: {
    id: number;
    nome: string;
    email?: string;
    telefone?: string;
    cpf_cnpj?: string;
    cidade?: string;
    estado?: string;
  };
  fornecedor?: {
    id: number;
    nome: string;
    email?: string;
    telefone?: string;
    cnpj?: string;
  };
  pedido?: {
    id: number;
    valor_total: number;
    status: string;
  };
  contrato?: {
    id: number;
    numero_contrato: string;
    valor: number;
    status: string;
  };
}

export type FinanceiroFiltroParams = {
  vencimentoPeriodo?: { dataInicio?: string; dataFim?: string } | null; // transacoes_financeiras.data_vencimento
  pagamentoPeriodo?: { dataInicio?: string; dataFim?: string } | null; // transacoes_financeiras.data_pagamento
  valorMin?: number | null;
  valorMax?: number | null;
  formaPagamento?: string | null;
  parcelas?: "todas" | "avista" | "parceladas" | null;
  estado?: string | null; // via cliente
  cidade?: string | null; // via cliente
  status?: "todos" | "pendente" | "pago" | "cancelado" | "vencido" | null;
  /** Restringe a pedidos/contratos deste vendedor (vendedor interno/externo) */
  vendedorId?: string | null;
} | null;

function transacoesFinanceirasSelect(filtros?: FinanceiroFiltroParams | null) {
  const clienteFilterPresent = !!(
    (filtros?.estado && filtros.estado.trim()) ||
    (filtros?.cidade && filtros.cidade.trim())
  );
  return `
    *,
    cliente:clientes${clienteFilterPresent ? "!inner" : ""}(*),
    fornecedor:fornecedores(id,nome,email,telefone,cnpj),
    pedido:pedidos(*),
    contrato:contratos!contrato_id(*)
  `;
}

export type FinanceiroSearchFilterOptions = {
  searchById?: number;
  searchOrFilter?: string;
};

/** Resolve busca textual antes de montar a query (evita await no builder thenable do Supabase). */
export async function resolveFinanceiroSearchOptions(
  searchTerm?: string
): Promise<FinanceiroSearchFilterOptions> {
  const term = searchTerm?.trim();
  if (!term) return {};

  if (!isNaN(Number(term))) {
    return { searchById: parseInt(term, 10) };
  }

  const searchLower = term.toLowerCase();
  const { data: clientesEncontrados } = await supabase
    .from("clientes")
    .select("id")
    .ilike("nome", `%${searchLower}%`);
  const { data: fornecedoresEncontrados } = await supabase
    .from("fornecedores")
    .select("id")
    .ilike("nome", `%${searchLower}%`);
  const clienteIds = clientesEncontrados?.map((c) => c.id) ?? [];
  const fornecedorIds = fornecedoresEncontrados?.map((f) => f.id) ?? [];
  const orParts = [`status.ilike.%${searchLower}%`, `forma_pagamento.ilike.%${searchLower}%`];
  if (clienteIds.length > 0) orParts.push(`cliente_id.in.(${clienteIds.join(",")})`);
  if (fornecedorIds.length > 0) orParts.push(`fornecedor_id.in.(${fornecedorIds.join(",")})`);
  return { searchOrFilter: orParts.join(",") };
}

/** Síncrono: o builder do Supabase é thenable; não usar async/await aqui. */
export function applyFinanceiroFiltrosToQuery(
  query: ReturnType<ReturnType<typeof supabase.from>["select"]>,
  filtros?: FinanceiroFiltroParams | null,
  options?: { tipo?: "entrada" | "saida" } & FinanceiroSearchFilterOptions
) {
  let q = query;

  if (options?.tipo) {
    q = q.eq("tipo", options.tipo);
  }

  if (filtros?.vencimentoPeriodo?.dataInicio) {
    q = q.gte("data_vencimento", filtros.vencimentoPeriodo.dataInicio);
  }
  if (filtros?.vencimentoPeriodo?.dataFim) {
    q = q.lte("data_vencimento", filtros.vencimentoPeriodo.dataFim);
  }
  if (filtros?.pagamentoPeriodo?.dataInicio) {
    q = q.gte("data_pagamento", filtros.pagamentoPeriodo.dataInicio);
  }
  if (filtros?.pagamentoPeriodo?.dataFim) {
    q = q.lte("data_pagamento", filtros.pagamentoPeriodo.dataFim);
  }

  if (typeof filtros?.valorMin === "number") q = q.gte("valor", filtros.valorMin);
  if (typeof filtros?.valorMax === "number") q = q.lte("valor", filtros.valorMax);

  if (filtros?.formaPagamento && filtros.formaPagamento.trim() && filtros.formaPagamento !== "todos") {
    q = q.ilike("forma_pagamento", filtros.formaPagamento.trim());
  }

  if (filtros?.parcelas === "parceladas") {
    q = q.gt("total_parcelas", 1);
  } else if (filtros?.parcelas === "avista") {
    q = q.or("total_parcelas.is.null,total_parcelas.lte.1");
  }

  if (filtros?.estado && filtros.estado.trim()) {
    q = q.eq("cliente.estado", filtros.estado.trim().toUpperCase());
  }
  if (filtros?.cidade && filtros.cidade.trim()) {
    q = q.eq("cliente.cidade", filtros.cidade.trim());
  }

  const status = filtros?.status ?? null;
  if (status && status !== "todos") {
    if (status === "vencido") {
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);
      const hojeStr = hoje.toISOString().split("T")[0];
      q = q.eq("status", "pendente").lt("data_vencimento", hojeStr);
    } else {
      q = q.eq("status", status);
    }
  }

  if (options?.searchById != null) {
    q = q.eq("id", options.searchById);
  } else if (options?.searchOrFilter) {
    q = q.or(options.searchOrFilter);
  }

  return q;
}

/** Busca todas as transações que correspondem aos filtros (para cards/resumo). */
export async function fetchTransacoesFinanceirasFiltradas(
  filtros?: FinanceiroFiltroParams | null,
  options?: { tipo?: "entrada" | "saida"; searchTerm?: string }
): Promise<TransacaoFinanceira[]> {
  const pageSize = 1000;
  let offset = 0;
  const all: TransacaoFinanceira[] = [];
  const searchOptions = await resolveFinanceiroSearchOptions(options?.searchTerm);

  let escopoOr: string | null = null;
  if (filtros?.vendedorId) {
    const { pedidoIds, contratoIds } = await fetchEscopoIdsVendedor(filtros.vendedorId);
    escopoOr = orFiltroEscopoPedidoContrato(pedidoIds, contratoIds);
    if (!escopoOr) return [];
  }

  // eslint-disable-next-line no-constant-condition
  while (true) {
    let query = supabase
      .from("transacoes_financeiras")
      .select(transacoesFinanceirasSelect(filtros));

    query = applyFinanceiroFiltrosToQuery(query, filtros, {
      tipo: options?.tipo,
      ...searchOptions,
    });
    if (escopoOr) query = query.or(escopoOr);
    const { data, error } = await query
      .order("data_vencimento", { ascending: true })
      .order("id", { ascending: true })
      .range(offset, offset + pageSize - 1);
    if (error) throw error;
    const rows = (data || []) as TransacaoFinanceira[];
    if (rows.length === 0) break;
    all.push(...rows);
    if (rows.length < pageSize) break;
    offset += pageSize;
  }

  return all;
}

export function useContasAReceberPaginated(
  page: number = 1,
  limit: number = 15,
  searchTerm?: string,
  filtros?: FinanceiroFiltroParams | null,
  enabled: boolean = true,
  sort?: FinanceiroSort | null
) {
  const { user, profile } = useAuth();
  const isScoped = isVendedorScopedRole(profile?.role) && !!user?.id;
  const filtrosComEscopo = useMemo(() => {
    if (!isScoped) return filtros ?? null;
    return { ...(filtros || {}), vendedorId: user!.id };
  }, [filtros, isScoped, user?.id]);

  return useQuery({
    queryKey: ["contas-a-receber", "unificado", page, limit, searchTerm, filtrosComEscopo ?? null, sort ?? null],
    enabled,
    queryFn: async () => {
      const transacoes = await fetchTransacoesFinanceirasFiltradas(filtrosComEscopo, {
        tipo: "entrada",
        searchTerm,
      });

      // Somente transações financeiras (sem parcelas de contrato)
      let merged = mergeContasAReceber([], transacoes);
      if (sort) {
        merged = sortFinanceiroLinhas(merged, sort);
      }
      const from = (page - 1) * limit;
      return {
        data: merged.slice(from, from + limit),
        count: merged.length,
      };
    },
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
    gcTime: 5 * 60 * 1000,
  });
}

export function useFormasPagamentoFinanceiro() {
  return useQuery({
    queryKey: ["transacoes_financeiras", "formas_pagamento_distinct"],
    queryFn: async () => {
      const pageSize = 1000;
      let offset = 0;
      const formas = new Set<string>();

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { data, error } = await supabase
          .from("transacoes_financeiras")
          .select("forma_pagamento")
          .not("forma_pagamento", "is", null)
          .range(offset, offset + pageSize - 1);
        if (error) throw error;
        const rows = (data || []) as Array<{ forma_pagamento: string | null }>;
        if (rows.length === 0) break;
        for (const r of rows) {
          const v = (r.forma_pagamento || "").trim();
          if (v) formas.add(v);
        }
        if (rows.length < pageSize) break;
        offset += pageSize;
      }

      return Array.from(formas).sort((a, b) => a.localeCompare(b, "pt-BR"));
    },
    staleTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

// Hook para contar total de transações financeiras
export function useTransacoesFinanceirasCount() {
  return useQuery({
    queryKey: ['transacoes_financeiras', 'count'],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('transacoes_financeiras')
        .select('*', { count: 'exact', head: true });

      if (error) {
        console.error('❌ Erro ao contar transações financeiras:', error);
        throw error;
      }

      return count || 0;
    },
    staleTime: 60 * 1000, // 1 minuto - contagem muda menos frequentemente
    refetchOnWindowFocus: false,
  });
}

// Hook para buscar transações financeiras paginadas
export function useTransacoesFinanceirasPaginated(
  page: number = 1,
  limit: number = 15,
  searchTerm?: string,
  tipo?: 'entrada' | 'saida',
  filtros?: FinanceiroFiltroParams,
  enabled: boolean = true,
  sort?: FinanceiroSort | null
) {
  return useQuery({
    queryKey: ['transacoes_financeiras', 'paginated', page, limit, searchTerm, tipo, filtros ?? null, sort ?? null],
    enabled,
    queryFn: async () => {
      console.log(`🔍 Buscando transações financeiras - Página ${page}, Limite ${limit}, Busca: ${searchTerm || 'nenhuma'}, Tipo: ${tipo || 'todos'}...`);
      
      try {
        const from = (page - 1) * limit;
        const to = from + limit - 1;
        const dbCol = sort ? financeiroSortToDbColumn(sort.field) : null;

        // Cliente/pedido/parcela: precisa ordenar em memória (join)
        if (sort && !dbCol) {
          const all = await fetchTransacoesFinanceirasFiltradas(filtros, { tipo, searchTerm });
          const linhas = all.map((t) => ({
            ...t,
            _origem: "transacao" as const,
          }));
          const sorted = sortFinanceiroLinhas(linhas, sort);
          return {
            data: sorted.slice(from, from + limit) as unknown as TransacaoFinanceira[],
            count: sorted.length,
          };
        }

        const searchOptions = await resolveFinanceiroSearchOptions(searchTerm);

        let query = supabase
          .from('transacoes_financeiras')
          .select(transacoesFinanceirasSelect(filtros), { count: 'exact' });

        query = applyFinanceiroFiltrosToQuery(query, filtros, { tipo, ...searchOptions });
        const orderCol = dbCol || 'created_at';
        const ascending = sort ? sort.direction === 'asc' : false;
        const { data, error, count } = await query
          .order(orderCol, { ascending })
          .order('id', { ascending: true })
          .range(from, to);

        if (error) {
          console.error('❌ Erro ao buscar transações financeiras:', error);
          throw error;
        }

        console.log(`✅ Transações financeiras encontradas na página ${page}:`, data?.length || 0, `Total: ${count || 0}`);
        return { data: ((data || []) as unknown) as TransacaoFinanceira[], count: count || 0 };
      } catch (error) {
        console.error('💥 Erro geral na busca de transações financeiras:', error);
        throw error;
      }
    },
    staleTime: 2 * 60 * 1000, // 2 minutos - financeiro pode ser atualizado mais frequentemente
    refetchOnWindowFocus: false, // Desabilitado para reduzir egresso
    gcTime: 5 * 60 * 1000, // 5 minutos de cache
  });
}

// Hook legado - retorno simplificado com array de transações
export function useTransacoesFinanceiras(limit: number = 200) {
  return useQuery({
    queryKey: ['transacoes_financeiras', 'default', limit],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('transacoes_financeiras')
        .select(`
          *,
          cliente:clientes(*),
          fornecedor:fornecedores(id,nome,email,telefone,cnpj),
          pedido:pedidos(*),
          contrato:contratos!contrato_id(*)
        `)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('❌ Erro ao buscar transações financeiras (hook simples):', error);
        throw error;
      }

      return (data || []) as TransacaoFinanceira[];
    },
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

// Hook para buscar as últimas transações financeiras pela atualização mais recente
export function useUltimasTransacoesFinanceiras(limit: number = 15, enabled = true) {
  return useQuery({
    queryKey: ['transacoes_financeiras', 'ultimas', limit],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('transacoes_financeiras')
        .select(`
          *,
          cliente:clientes(*),
          fornecedor:fornecedores(id,nome,email,telefone,cnpj),
          pedido:pedidos(*),
          contrato:contratos!contrato_id(*)
        `)
        .order('updated_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('❌ Erro ao buscar últimas transações financeiras:', error);
        throw error;
      }

      return (data || []) as TransacaoFinanceira[];
    },
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useCriarTransacaoFinanceira() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (transacaoData: {
      pedido_id?: number;
      contrato_id?: number;
      sinistro_id?: number;
      cliente_id?: number | null;
      fornecedor_id?: number | null;
      tipo: 'entrada' | 'saida';
      valor: number;
      forma_pagamento: string;
      data_vencimento: string;
      observacoes?: string;
      numero_parcela?: number;
      total_parcelas?: number;
    }) => {
      const selectQuery = `
          *,
          cliente:clientes(*),
          fornecedor:fornecedores(id,nome,email,telefone,cnpj),
          pedido:pedidos(*),
          contrato:contratos!contrato_id(*)
        `;

      const buildPayload = (includeSinistroId: boolean) => {
        const { sinistro_id, ...rest } = transacaoData;
        return {
          ...rest,
          ...(includeSinistroId && sinistro_id != null ? { sinistro_id } : {}),
          status: 'pendente' as const,
        };
      };

      const tryInsert = async (includeSinistroId: boolean) =>
        supabase
          .from('transacoes_financeiras')
          .insert(buildPayload(includeSinistroId))
          .select(selectQuery)
          .single();

      console.log('Criando transação financeira:', buildPayload(true));

      let { data, error } = await tryInsert(true);

      const sinistroColumnMissing =
        error?.code === 'PGRST204' &&
        (error.message?.includes('sinistro_id') ?? false);

      if (sinistroColumnMissing && transacaoData.sinistro_id != null) {
        console.warn(
          'Coluna sinistro_id ausente; criando transação sem vínculo direto (referência nas observações).'
        );
        ({ data, error } = await tryInsert(false));
      }

      if (error) {
        console.error('Erro ao criar transação:', error);
        throw error;
      }

      console.log('Transação criada com sucesso:', data);
      return {
        ...data,
        _sinistroIdOmitido: sinistroColumnMissing,
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transacoes_financeiras'] });
      queryClient.invalidateQueries({ queryKey: ['contas-a-receber'] });
      queryClient.invalidateQueries({ queryKey: ['pedidos'] });
      queryClient.invalidateQueries({ queryKey: ['contratos'] });
      queryClient.invalidateQueries({ queryKey: ['agenda'] });
    }
  });
}

export function useAtualizarTransacaoFinanceira() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updateData }: { 
      id: number; 
      status?: string;
      data_vencimento?: string;
      data_pagamento?: string | null;
      observacoes?: string;
      nota_fiscal_url?: string | null;
      nota_fiscal_name?: string | null;
      notas_fiscais?: Array<{ url: string; nome: string }>;
    }) => {
      const { notas_fiscais, ...rest } = updateData;

      const runUpdate = async (includeNotasFiscais: boolean) =>
        supabase
          .from('transacoes_financeiras')
          .update({
            ...rest,
            ...(includeNotasFiscais && notas_fiscais !== undefined ? { notas_fiscais } : {}),
          })
          .eq('id', id)
          .select()
          .single();

      let { data, error } = await runUpdate(true);

      const notasFiscaisColumnMissing =
        error?.code === 'PGRST204' &&
        (error.message?.includes('notas_fiscais') ?? false);

      if (notasFiscaisColumnMissing && notas_fiscais !== undefined) {
        ({ data, error } = await runUpdate(false));
      }

      if (error) {
        console.error('Erro na atualização da transação:', error);
        throw error;
      }

      if (notasFiscaisColumnMissing && notas_fiscais !== undefined && data) {
        return { ...data, notas_fiscais, _notasFiscaisOmitido: true };
      }

      // Ao marcar entrada como paga: ativar seguro/status do contrato e renovação
      if (data?.status === 'pago' && data?.tipo === 'entrada') {
        const dataPag = data?.data_pagamento || new Date().toISOString().split('T')[0];
        let contratoIdParaAtivar = data.contrato_id as number | null | undefined;

        if (!contratoIdParaAtivar && data.pedido_id) {
          const { data: contratoDoPedido } = await supabase
            .from('contratos')
            .select('id, seguro_ativado')
            .eq('pedido_id', data.pedido_id)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();
          if (contratoDoPedido && !contratoDoPedido.seguro_ativado) {
            contratoIdParaAtivar = contratoDoPedido.id;
          }
        }

        if (contratoIdParaAtivar) {
          const { data: contrato } = await supabase
            .from('contratos')
            .select('seguro_ativado')
            .eq('id', contratoIdParaAtivar)
            .single();
          if (contrato && !contrato.seguro_ativado) {
            try {
              await ativarSeguroAposPrimeiraParcela(contratoIdParaAtivar, dataPag);
            } catch (e) {
              console.error('Erro ao ativar seguro/renovação:', e);
            }
          }
        } else if (data.pedido_id && data.cliente_id) {
          // Sem contrato: primeira receita paga do pedido ativa renovação na agenda
          const { count } = await supabase
            .from('transacoes_financeiras')
            .select('*', { count: 'exact', head: true })
            .eq('pedido_id', data.pedido_id)
            .eq('tipo', 'entrada')
            .eq('status', 'pago');
          if (count === 1) {
            try {
              await criarEventoRenovacaoAgenda(Number(data.cliente_id), dataPag, {
                pedidoId: data.pedido_id
              });
            } catch (e) {
              console.error('Erro ao criar evento de renovação (pedido):', e);
            }
          }
        }
      }

      return data;
    },
    onSuccess: (_data) => {
      queryClient.invalidateQueries({ queryKey: ['transacoes_financeiras'] });
      queryClient.invalidateQueries({ queryKey: ['contas-a-receber'] });
      queryClient.invalidateQueries({ queryKey: ['contratos'] });
      queryClient.invalidateQueries({ queryKey: ['agenda'] });
    }
  });
}

export function useExcluirTransacaoFinanceira() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (transacaoId: number) => {
      const { error } = await supabase.rpc('excluir_transacao_financeira', {
        p_id: transacaoId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transacoes_financeiras'] });
      queryClient.invalidateQueries({ queryKey: ['contas-a-receber'] });
      queryClient.invalidateQueries({ queryKey: ['parcelas'] });
      queryClient.invalidateQueries({ queryKey: ['contratos'] });
    },
  });
}
